import React, { useEffect, useReducer } from 'react';

import _ from 'lodash';
import { Link, Prompt } from 'react-router-dom';
import { Button, FormControl, InputLabel, MenuItem } from '@material-ui/core';
import { Paper, TextField } from '@material-ui/core';

import API from 'utils/api';
import { useSources } from 'hooks';
import { ButtonContainer, DatePicker, ErrorDialog, Form } from 'components';
import { Media, PageTitle, Progress, Select } from 'components';
import { backLink, classes } from 'utils';
import { defaultValues, Status, validate } from './helpers';

import TriggerSelector from '../MessageFlow/TriggerSelector';
import Participants from './Participants';
import Results from './Results';
import NextStageDialog from './NextStageDialog';

import styles from './DrawManager.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT':
      return {
        id: state.id,
        status: Status[_.get(action, 'payload.status')],
        initialValues: _.merge(
          {},
          defaultValues,
          _.omitBy(_.pick(action.payload, _.keys(defaultValues)), _.isNil)
        ),
        results: _.get(action, 'payload.results'),
        dialog: {},
      };

    case 'ERROR':
      return { ...state, dialog: { error: action.payload } };

    case 'STATUS': {
      const status = action.payload;

      return status !== state.status && _.findKey(Status, it => it === status)
        ? { ...state, status, dialog: {} }
        : _.isEmpty(state.dialog)
        ? state
        : { ...state, dialog: {} };
    }

    case 'PROMOTE':
      return { ...state, dialog: { promote: action.payload } };

    case 'CLOSE_DIALOG':
      return _.isEmpty(state.dialog) ? state : { ...state, dialog: {} };

    case 'RESULTS': {
      const { status, results } = action.payload;

      return status !== state.status && _.findKey(Status, it => it === status)
        ? { ...state, status, results, dialog: {} }
        : _.isEmpty(state.dialog)
        ? state
        : { ...state, dialog: {} };
    }

    case 'REDEEM': {
      const { ticketId, value } = action.payload;
      const ticket = _.find(state.results, ['winner.ticketId', ticketId]);

      if (ticket && ticket.redeemed !== !!value) {
        ticket.redeemed = !!value;
        return { ...state };
      }

      return state;
    }

    default:
      return state;
  }
};

const DrawManager = props => {
  const [state, dispatch] = useReducer(reducer, {
    id: _.get(props, 'match.params.id'),
    loading: true,
  });

  const { id, loading, status, initialValues, dialog } = state;
  const sources = useSources();

  useEffect(() => {
    if (id) {
      API.getDraw(id, { parseTags: true }, sources).then(({ data, error }) =>
        dispatch({ type: 'INIT', payload: data && !error ? data : null })
      );
    }
  }, [sources, id]);

  if (loading) {
    return <Progress />;
  }

  if (_.isNil(status)) {
    return (
      <ErrorDialog
        open={true}
        message="Could not load the draw!"
        onClose={() => props.history.push(backLink(props, 2))}
      />
    );
  }

  const onSubmit = async (values, form) => {
    const { local, file } = values.image || {};

    const image =
      local instanceof File
        ? {
            contentType: local.type,
            base64Data: await API.loadFileBase64(local),
          }
        : file
        ? undefined
        : null;

    const data = {
      image,
      description: values.description || undefined,
      date: values.date || null,
      numPrizes: values.numPrizes,
      ticketPolicy: values.ticketPolicy,
      tags: _.map(values.tags, 'value.id'),
    };

    const response = await API.updateDraw(id, data, sources);
    form.setSubmitting(false);

    parseInt(response.status / 100) === 2
      ? form.resetForm({ values })
      : dispatch({ type: 'ERROR', payload: response.error });
  };

  const renderControls = ({ fieldProps, values, setFieldValue, dirty }) => {
    const action = [
      ['open', 'Start issuing tickets'],
      ['close', 'End ticketing period'],
      ['draw', 'Find winners'],
      ['end', 'Finish the draw'],
    ][status];

    return (
      <Paper className={classes(styles.paper, styles.controls)}>
        <div>
          <div className={styles.image}>
            <Media
              style={{ width: 111, height: 111 }}
              autoUpload={false}
              mediaFile={_.get(values, 'image.file')}
              mediaUrl={_.get(values, 'image.url')}
              localMediaFile={_.get(values, 'image.local')}
              onChange={({ mediaFile: file, mediaUrl: url, localMediaFile: local }) =>
                setFieldValue('image', _.omitBy({ file, url, local }, _.isNil))
              }
            />
            <span>Image</span>
          </div>

          <div>
            <TextField
              {...fieldProps('description', 'Description')}
              inputProps={{ maxLength: 256 }}
              multiline
              minRows={4}
              maxRows={4}
            />

            <div style={{ width: 196 }}>
              <DatePicker
                {...fieldProps('date', 'Draw date')}
                inputProps={{ readOnly: status > Status.closed }}
              />
            </div>
          </div>
        </div>
        <div>
          <FormControl fullWidth>
            <InputLabel shrink>Current status</InputLabel>
            <div className={styles.status}>
              <span>{_.findKey(Status, it => it === status)}</span>
              {action ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => dispatch({ type: 'PROMOTE', payload: values })}
                  title={action[1]}
                  disabled={dirty}
                >
                  {action[0]}
                </Button>
              ) : null}
            </div>
          </FormControl>

          <TextField
            {...fieldProps('numPrizes', 'Number of prizes')}
            type="number"
            inputProps={{ min: 1 }}
            InputProps={{ readOnly: status > Status.closed }}
            style={status > Status.closed ? { pointerEvents: 'none' } : undefined}
          />

          <Select
            {...fieldProps('ticketPolicy', 'Ticket policy')}
            inputProps={{ readOnly: status > Status.idle }}
          >
            <MenuItem value="once">Once</MenuItem>
            <MenuItem value="onceTag">Once per tag</MenuItem>
            <MenuItem value="unlimited">Unlimited</MenuItem>
          </Select>
        </div>
        <div>
          <TriggerSelector
            {...fieldProps('tags', 'Tags')}
            inputProps={{ readOnly: status > Status.open }}
          />
        </div>
      </Paper>
    );
  };

  return (
    <div className={styles.manager}>
      <div className={styles.topbar}>
        <PageTitle>Draw Manager</PageTitle>

        <ButtonContainer position="top">
          <Button component={Link} to={backLink(props, 2)}>
            back to draw list
          </Button>
        </ButtonContainer>
      </div>

      <Form
        className={styles.mainForm}
        validationSchema={validate}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {form => (
          <>
            {renderControls(form)}

            <ButtonContainer position="top">
              {form.dirty ? (
                <Button onClick={() => form.resetForm({ values: form.initialValues })}>
                  cancel
                </Button>
              ) : null}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={form.isSubmitting || !form.dirty}
              >
                save
              </Button>
            </ButtonContainer>

            <div className={styles.info}>
              <Paper className={styles.paper}>
                <Participants status={status} drawId={id} />
              </Paper>

              <Paper className={styles.paper}>
                <Results state={state} dispatch={dispatch} />
              </Paper>
            </div>

            <Prompt
              when={form.dirty}
              message="You are leaving the page with unsaved fields. Are you sure you want to leave?"
            />
          </>
        )}
      </Form>

      <NextStageDialog
        open={!!dialog.promote}
        onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
        state={state}
        dispatch={dispatch}
        values={dialog.promote}
      />

      <ErrorDialog
        open={!!dialog.error}
        onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
        message={dialog.error}
      />
    </div>
  );
};

export default DrawManager;
